import { useEffect, useState } from "react";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Smartphone, CreditCard, Check, Coins } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PRESETS = [5, 10, 25, 50, 100];

export const BuyPointsDialog = ({ open, onClose, onCredited, pointValueFrw = 10 }) => {
  const enabledMethods = usePaymentMethods("points", open);
  const { isAuthed } = useAuth();
  const [points, setPoints] = useState(10);
  const [method, setMethod] = useState("momo");
  const [cur, setCur] = useState("RWF");
  const [momoPhone, setMomoPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  useEffect(() => { if (open) { setPoints(10); setMethod("momo"); setCur("RWF"); setBusy(false); setPolling(false); } }, [open]);

  const frw = points * pointValueFrw;
  const usd = (frw / 1300).toFixed(2);
  const priceLabel = method === "stripe" && cur !== "RWF"
    ? (cur === "EUR" ? `€${(frw / 1400).toFixed(2)}` : `$${usd}`)
    : `${frw.toLocaleString()} RWF`;

  const pollMomo = async (ref) => {
    setPolling(true);
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const { data } = await endpoints.pointsMomoStatus(ref);
        if (data.paid || data.status === "successful") {
          toast.success(`${points} points added! 🎉`);
          setPolling(false); onCredited && onCredited(); onClose();
          return;
        }
        if (data.status === "failed") { toast.error("Payment failed or declined."); setPolling(false); return; }
      } catch { /* keep polling */ }
    }
    setPolling(false);
    toast.message("Still pending — your points will appear once payment confirms.");
  };

  const pay = async () => {
    if (!isAuthed) { window.dispatchEvent(new Event("open-auth")); return; }
    if (points < 1) { toast.error("Choose at least 1 point"); return; }
    setBusy(true);
    try {
      if (method === "stripe") {
        const origin = window.location.origin;
        const { data } = await endpoints.pointsPurchase({
          points, method: "stripe", currency: cur.toLowerCase(),
          success_url: `${origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/vote`,
        });
        window.location.href = data.url || data.checkout_url;
      } else {
        if (momoPhone.replace(/[^\d]/g, "").length < 9) { toast.error("Enter your MoMo phone number"); setBusy(false); return; }
        const { data } = await endpoints.pointsPurchase({ points, method: "momo", phone: momoPhone });
        toast.success("Approve the payment on your phone (MoMo PIN)…");
        await pollMomo(data.reference);
      }
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const methods = [
    { id: "momo", label: "MTN MoMo", desc: `Mobile Money · ${frw.toLocaleString()} RWF`, icon: Smartphone },
    { id: "stripe", label: "Card", desc: `Visa / Mastercard · ${priceLabel}`, icon: CreditCard },
  ].filter((m) => !enabledMethods || enabledMethods.includes(m.id));
  const firstMethod = methods[0]?.id;
  useEffect(() => {
    if (open && firstMethod && !methods.some((m) => m.id === method)) setMethod(firstMethod);
  }, [open, firstMethod, method]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="buy-points-dialog">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !busy && !polling && onClose()} />
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            className="relative w-full max-w-md glass border border-[#273042] rounded-2xl p-7 shadow-2xl">
            <button onClick={() => !busy && !polling && onClose()} className="absolute right-4 top-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 text-white"><Coins className="w-6 h-6 text-[#F5B301]" /><h2 className="text-2xl font-extrabold">Buy voting points</h2></div>
            <p className="text-slate-400 text-sm mt-1">1 point = {pointValueFrw} FRW. Use points to vote in paid campaigns.</p>

            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-5 mb-2">Amount</div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p} onClick={() => setPoints(p)} data-testid={`points-preset-${p}`}
                  className={`flex-1 min-w-[56px] h-10 rounded-lg text-sm font-bold border transition-colors ${points === p ? "border-[#F5B301] bg-[#F5B301]/10 text-white" : "border-[#273042] bg-[#0A0C10] text-slate-300 hover:text-white"}`}>{p}</button>
              ))}
            </div>
            <input type="number" min={1} value={points} onChange={(e) => setPoints(Math.max(1, parseInt(e.target.value || "1", 10)))}
              data-testid="points-custom-input" className="mt-2 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white text-sm outline-none focus:border-[#F5B301]/60" />

            <div className="mt-4 flex items-baseline justify-between rounded-xl bg-[#0A0C10] border border-[#273042] p-4">
              <div className="text-white font-bold">{points} points</div>
              <div className="text-2xl font-extrabold text-[#F5B301]" data-testid="points-price">{priceLabel}</div>
            </div>

            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-6 mb-2">Payment method</div>
            <div className="space-y-2">
              {methods.map((g) => (
                <button key={g.id} onClick={() => setMethod(g.id)} data-testid={`points-method-${g.id}`}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-colors text-left ${method === g.id ? "border-[#F5B301] bg-[#F5B301]/10" : "border-[#273042] bg-[#0A0C10] hover:border-slate-600"}`}>
                  <g.icon className="w-5 h-5 text-slate-300" />
                  <div className="flex-1"><div className="text-white text-sm font-semibold">{g.label}</div><div className="text-xs text-slate-400">{g.desc}</div></div>
                  {method === g.id && <Check className="w-4 h-4 text-[#F5B301]" />}
                </button>
              ))}
            </div>

            {method === "stripe" && (
              <div className="flex gap-2 mt-3" data-testid="points-currency">
                {["RWF", "USD", "EUR"].map((c) => (
                  <button key={c} onClick={() => setCur(c)} className={`flex-1 h-9 rounded-lg text-xs font-bold border ${cur === c ? "border-[#F5B301] bg-[#F5B301]/10 text-white" : "border-[#273042] bg-[#0A0C10] text-slate-300"}`}>{c}</button>
                ))}
              </div>
            )}
            {method === "momo" && (
              <input value={momoPhone} onChange={(e) => setMomoPhone(e.target.value)} data-testid="points-momo-phone"
                placeholder="MoMo phone e.g. +250788123456" inputMode="tel"
                className="mt-3 w-full h-12 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#F5B301]/60" />
            )}

            <button onClick={pay} disabled={busy || polling} data-testid="points-pay-btn"
              className="mt-6 w-full h-12 rounded-xl bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {(busy || polling) ? <><Loader2 className="w-5 h-5 animate-spin" /> {polling ? "Waiting for approval…" : "Processing…"}</> : `Pay ${priceLabel}`}
            </button>
            <p className="mt-3 text-[11px] text-slate-500 text-center">Secure payment · points added instantly on confirmation · receipt sent via WhatsApp</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
